import { getFrontendConfig } from "../../utils/Utils";

/**
 * Generic http client with error handling.
 */
export default class HttpClient {
    baseUrl = "";

    constructor() {
        this.baseUrl = getFrontendConfig().apiUrl;
    }

    /**
     * Performs an HTTP GET call returning a promise with data or an error promise.
     *
     * @param {*} target relative url
     */
    async get(target: string) {
        try {
            const response = await fetch(this.buildUrl(target), {
                credentials: "include", // "include" means append cookies to requests
            });
            this.assertSuccessful(response);
            return response.json();
        } catch (e: any) {
            throw {
                name: "Connection error",
                message: e.message ?? "Please check your connection.",
                status: 0,
            } as HttpError;
        }
    }

    /**
     * Performs an HTTP POST call returning a promise with data or an error promise.
     *
     * @param {*} data the data in Json format
     * @param {*} target relative url
     */
    async post(data: string | object, target: string) {
        try {
            const response = await fetch(this.buildUrl(target), {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                },
                body: JSON.stringify(data),
            });
            this.assertSuccessful(response);
            if (response.status === 204) {
                return "";
            }
            return response.json();
        } catch (e: any) {
            throw {
                name: "Connection error",
                message: e.message ?? "Please check your connection.",
                status: 0,
            } as HttpError;
        }
    }

    /**
     * Creates a full url from relative one, using configured baseUrl.
     * @param target relative url
     */
    buildUrl(target: string) {
        return `${this.baseUrl}${target}`;
    }

    assertSuccessful(response: Response) {
        if (!response.ok) {
            let error: HttpError = {
                name: "Invalid response error",
                message:
                    "Errors.You cannot extend any mortgages at the moment, please contact your client advisor for details.",
                status: 0,
            };
            if (response.status === 401) {
                error.message = "Errors.User is not logged in!";
            } else if (response.status === 403) {
                error.message = "Errors.This functionality is not available for login on behalf users.";
            } else if (response.status === 404) {
                error.message = "Errors.No data found!";
            } else if (response.status === 504) {
                error.message =
                    "Errors.You cannot extend any mortgages at the moment, please contact your client advisor for details.";
            }
            error.status = response.status;
            throw error;
        }
    }
}

export interface HttpError extends Error {
    status: number;
}
